import { normalizePhoneKey } from 'app/formatPhoneNumber'
import type { Contact, Message } from 'app/generated/graphql'
import { buildSearchTargetClause } from 'app/lib/inboxSearch'
import { phonePairKey } from 'app/lib/smsThreadId'

/** A conversation as the palette shows it: its newest message, and the contact behind it when known. */
export type PaletteThread = {
  threadId: string
  message: Message
  contact?: Contact
}

/**
 * Phone numbers to look for in `from`/`to`, taken from the contacts the query already
 * matched. Raw values, not normalised: the inbox passes `cellPhone` through as stored
 * and the index matches it exactly. Duplicates by last-ten digits collapse to one.
 */
export function contactPhones(contacts: Contact[]): string[] {
  const seen = new Set<string>()
  const phones: string[] = []
  for (const contact of contacts) {
    for (const phone of [contact.cellPhone, contact.homePhone, contact.workPhone]) {
      if (!phone) continue
      const key = normalizePhoneKey(phone)
      if (!key || seen.has(key)) continue
      seen.add(key)
      phones.push(phone)
    }
  }
  return phones
}

/** Messages `search` string, or undefined when there is no phone to match on. */
export function threadsSearchTerm(phones: string[], digits: string): string | undefined {
  return buildSearchTargetClause({ phones, digits })
}

/**
 * Collapse a newest-first message list into one entry per conversation, keeping the
 * first (newest) message seen for each. Grouping mirrors the inbox: the normalised
 * phone pair, falling back to the raw thread id when a number is unusable.
 */
export function latestPerThread(messages: Message[], contacts: Contact[], limit: number): PaletteThread[] {
  const threads: PaletteThread[] = []
  const seen = new Set<string>()
  for (const message of messages) {
    if (!message.threadId) continue
    const key = phonePairKey(message.from, message.to) ?? message.threadId
    if (seen.has(key)) continue
    seen.add(key)
    threads.push({ threadId: message.threadId, message, contact: contactFor(message, contacts) })
    if (threads.length === limit) break
  }
  return threads
}

/** The other party's number: whichever side of the message is not the practice. */
export function counterpartPhone(message: Message): string {
  const outbound = !!message.sentByUserId || !!message.isAgent || message.direction === `outbound`
  return outbound ? message.to : message.from
}

function contactFor(message: Message, contacts: Contact[]): Contact | undefined {
  if (message.contactId) {
    const byId = contacts.find((contact) => contact.id === message.contactId)
    if (byId) return byId
  }
  const key = normalizePhoneKey(counterpartPhone(message))
  if (!key) return undefined
  return contacts.find((contact) =>
    [contact.cellPhone, contact.homePhone, contact.workPhone].some(
      (phone) => normalizePhoneKey(phone ?? undefined) === key,
    ),
  )
}
